import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import useAudioManager from "../customhook/useAudioManager";
import { useAudio } from "../context/AudioContext";

const AudioManagerContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1000;

  /* ID para seleção em media queries */
  id: "audio-manager-container";

  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
  }
`;

const AudioButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(26, 26, 26, 0.7);
  border: 1px solid var(--color-primary);
  color: var(--color-white);
  font-size: 1.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 144, 82, 0.2);
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
`;

const VolumeControls = styled(motion.div)`
  position: absolute;
  top: 50px;
  left: 0;
  background: rgba(26, 26, 26, 0.9);
  border-radius: 10px;
  padding: 1rem;
  width: 200px;
  border: 1px solid var(--color-primary);
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    top: 60px;
    width: 250px;
    padding: 1.2rem;
  }
`;

const VolumeSlider = styled.div`
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const SliderLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  color: var(--color-white);
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
  }
`;

const Slider = styled.input`
  width: 100%;
  -webkit-appearance: none;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
    box-shadow: 0 0 5px rgba(255, 144, 82, 0.5);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
    box-shadow: 0 0 5px rgba(255, 144, 82, 0.5);
    border: none;
  }

  @media (max-width: 768px) {
    height: 8px;

    &::-webkit-slider-thumb {
      width: 24px;
      height: 24px;
    }

    &::-moz-range-thumb {
      width: 24px;
      height: 24px;
    }
  }
`;

const AudioManager: React.FC = () => {
  const {
    musicVolume,
    sfxVolume,
    isMuted,
    toggleMute,
    updateMusicVolume,
    updateSfxVolume,
  } = useAudio() as {
    musicVolume: number;
    sfxVolume: number;
    isMuted: boolean;
    toggleMute: () => void;
    updateMusicVolume: (value: number) => void;
    updateSfxVolume: (value: number) => void;
  };

  const [showControls, setShowControls] = useState(false);

  const toggleControls = () => setShowControls((prev) => !prev);

  return (
    <AudioManagerContainer>
      <AudioButton
        onClick={toggleControls}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isMuted ? "🔇" : "🔊"}
      </AudioButton>

      {showControls && (
        <VolumeControls
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          <VolumeSlider>
            <SliderLabel>
              <span>Music</span>
              <span>{musicVolume}%</span>
            </SliderLabel>
            <Slider
              type="range"
              min="0"
              max="100"
              value={musicVolume}
              onChange={(e) => updateMusicVolume(parseInt(e.target.value))}
            />
          </VolumeSlider>

          <VolumeSlider>
            <SliderLabel>
              <span>Sound Effects</span>
              <span>{sfxVolume}%</span>
            </SliderLabel>
            <Slider
              type="range"
              min="0"
              max="100"
              value={sfxVolume}
              onChange={(e) => updateSfxVolume(parseInt(e.target.value))}
            />
          </VolumeSlider>

          <AudioButton
            onClick={toggleMute}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{ width: "100%", borderRadius: "8px" }}
          >
            {isMuted ? "Ativar Som" : "Mudo"}
          </AudioButton>
        </VolumeControls>
      )}
    </AudioManagerContainer>
  );
};

export default AudioManager;
